import { useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import {
  Flex,
  PrimaryButton,
  type SegmentedControlOption,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'

import { AccessibleSegmentedControl } from '~/components/common/AccessibleSegmentedControl'
import { type CivilDate } from '~/models/civilDate'
import { daysOfMonth } from '~/pages/SplitSupplyPlan/models/period'
import { DayNav } from '~/pages/SplitSupplyPlanDetail/components/DayNav'
import { type NoticeView } from '~/pages/SplitSupplyPlanDetail/hooks/useNoticeDetailView'

const NS = 'page:splitSupplyPlanDetail' as const

type Props = {
  isMonthly: boolean
  view: NoticeView
  onSetView: (next: NoticeView) => void
  /** 未保存の変更があれば確認を挟んでから実行する (`useNoticeDraftEditing` の `guard.guardAction`)。 */
  guardAction: (action: () => void) => void
  wholeMonth: boolean
  deliveryDate: CivilDate | undefined
  /** 月間の対象月 (日送りの範囲)。 */
  target: CivilDate | undefined
  onDayChange: (day: CivilDate) => void
  isEditing: boolean
  invalidCount: number
  isSaving: boolean
  onDiscard: () => void
  onSave: () => void
  /** 閲覧中 (編集中でない時) の操作 (「編集」 など)。 通ごとに違う。 */
  viewActions: React.ReactNode
}

/**
 * 詳細の操作の行: 左に 「1 日 / 月全体」 と日送り (月間の通だけ)、 右に編集中の 「破棄」 「保存」
 * (正しくない値の数つき) か、 閲覧中の操作。 自社の通告値と他社の通告値の詳細で共通。
 */
export const NoticeDetailToolbar = ({
  isMonthly,
  view,
  onSetView,
  guardAction,
  wholeMonth,
  deliveryDate,
  target,
  onDayChange,
  isEditing,
  invalidCount,
  isSaving,
  onDiscard,
  onSave,
  viewActions,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const monthDays = useMemo(
    () => (isMonthly && target !== undefined ? daysOfMonth(target) : []),
    [isMonthly, target],
  )
  const viewOptions: Array<SegmentedControlOption<NoticeView>> = [
    { value: 'day', label: t(`${NS}.view.day`) },
    { value: 'month', label: t(`${NS}.view.month`) },
  ]
  return (
    <Flex alignItems="center" flexWrap="wrap" gap={3}>
      {isMonthly && (
        <AccessibleSegmentedControl
          aria-label={t(`${NS}.view.label`)}
          name="split-supply-detail-view"
          onSelect={(next) => {
            // 「1 日 / 月全体」 の切り替え。 未保存の変更があれば確認してから切り替える
            if (next !== view) guardAction(() => onSetView(next))
          }}
          options={viewOptions}
          selected={view}
        />
      )}
      {isMonthly && !wholeMonth && deliveryDate !== undefined && (
        <DayNav day={deliveryDate} days={monthDays} onChange={onDayChange} />
      )}
      <Flex flex="1" />
      {isEditing ? (
        <>
          {invalidCount > 0 && (
            <Text textStyle="caption" color="Text.negativeMid">
              {t(`${NS}.save.invalidCells`, { count: invalidCount })}
            </Text>
          )}
          <TertiaryButton disabled={isSaving} onClick={onDiscard} size="md">
            {t(`${NS}.discard`)}
          </TertiaryButton>
          <PrimaryButton
            disabled={invalidCount > 0 || isSaving}
            loading={isSaving}
            onClick={onSave}
            size="md"
          >
            {t(`${NS}.save.button`)}
          </PrimaryButton>
        </>
      ) : (
        viewActions
      )}
    </Flex>
  )
}
